import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAgentExecution, type AgentRun } from "@/contexts/AgentExecutionContext";
import { AGENT_CATALOG } from "@/lib/agent-catalog";

function statusLabel(status: AgentRun["status"]) {
  switch (status) {
    case "loading":
      return "Cargando";
    case "streaming":
      return "En progreso";
    case "completed":
      return "Completado";
    case "rejected":
      return "Rechazado";
    default:
      return "Error";
  }
}

export default function MobileAgentHistoryPage() {
  const { agentSlug } = useParams<{ agentSlug: string }>();
  const { runs } = useAgentExecution();
  const [selectedRun, setSelectedRun] = useState<AgentRun | null>(null);

  if (!agentSlug || !AGENT_CATALOG[agentSlug]) {
    return <Navigate to="/dashboard/agents/mobile" replace />;
  }

  const filteredRuns = useMemo(() => {
    return Object.values(runs)
      .filter((run) => {
        if (agentSlug === "specialized") {
          return run.id.startsWith("specialized-");
        }
        return run.id.includes(agentSlug);
      })
      .sort((a, b) => {
        const at = a.messages[a.messages.length - 1]?.timestamp || "";
        const bt = b.messages[b.messages.length - 1]?.timestamp || "";
        return bt.localeCompare(at);
      });
  }, [agentSlug, runs]);

  return (
    <div className="p-4 sm:p-6">
      <Link to={`/dashboard/agents/mobile/${agentSlug}`} className="mb-4 inline-flex items-center text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="mr-1 h-4 w-4" /> Volver
      </Link>
      <div className="mb-4 flex items-center gap-2">
        <h1 className="text-lg font-semibold">Historial y estado</h1>
        <Badge variant="secondary">{filteredRuns.length} runs</Badge>
      </div>

      <div className="space-y-3">
        {filteredRuns.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-sm text-slate-500">Aun no hay ejecuciones para este agente.</CardContent>
          </Card>
        )}
        {filteredRuns.map((run) => {
          const lastMessage = run.messages[run.messages.length - 1];
          const timestamp = lastMessage?.timestamp ? new Date(lastMessage.timestamp).toLocaleString() : "Sin fecha";

          return (
            <Card key={run.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{run.id}</CardTitle>
                  <Badge variant="outline">{statusLabel(run.status)}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <Clock3 className="h-3 w-3" /> {timestamp}
                </div>
                <Button variant="outline" size="sm" className="w-full" onClick={() => setSelectedRun(run)}>
                  Ver detalle
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Sheet open={!!selectedRun} onOpenChange={() => setSelectedRun(null)}>
        <SheetContent side="bottom" className="max-h-[80vh]">
          <SheetHeader>
            <SheetTitle>{selectedRun?.id}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3 overflow-y-auto text-sm">
            <p className="text-slate-600">Estado: {selectedRun ? statusLabel(selectedRun.status) : "-"}</p>
            <div className="space-y-2">
              {(selectedRun?.messages || []).slice(-8).map((message, idx) => (
                <div key={`${message.role}-${idx}`} className="rounded-lg border border-slate-200 p-2">
                  <div className="mb-1 text-xs uppercase text-slate-500">{message.role}</div>
                  <div>{message.content}</div>
                </div>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
